import React from "react";

// Known regulations / policy instruments to auto-bold in AI narrative text.
const REGULATIONS = [
  "Section 301 tariffs", "Section 301",
  "Foreign Direct Product Rule", "FDPR",
  "GDPR",
  "RUPTL",
  "Uyghur Forced Labor Prevention Act", "UFLPA",
  "CHIPS Act", "CHIPS and Science Act",
  "Entity List",
  "CSDDD",
  "Modern Slavery Act",
  "OFAC", "EAR", "ITAR",
  "Inflation Reduction Act", "IRA",
  "Carbon Border Adjustment Mechanism", "CBAM",
  "AI Act", "EU AI Act",
  "Digital Services Act", "DSA",
  "Digital Markets Act", "DMA",
  "PIPL",
  "FIRB",
  "CFIUS",
];

const REGEX_REG = new RegExp(
  "\\b(" + REGULATIONS.map((r) => r.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")).join("|") + ")\\b",
  "g"
);
const REGEX_PERCENT_OR_MONEY = /\b((?:USD\s?)?\$?\s?\d[\d,.]*(?:\s?(?:%|USD|bn|m))?)\b/g;

/**
 * Render a string with key terms / numbers / percentages bolded.
 * Returns React fragments.
 */
export const renderBolded = (text) => {
  if (!text) return null;
  // Tokenize by regulation matches and number/percent matches.
  const pieces = [];
  let last = 0;
  // Combine both regexes by walking the string and finding next match
  const regs = [];
  let m;
  while ((m = REGEX_REG.exec(text)) !== null) {
    regs.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
  }
  const nums = [];
  while ((m = REGEX_PERCENT_OR_MONEY.exec(text)) !== null) {
    const v = m[0];
    if (/[%$]|USD|bn|m/.test(v)) {
      nums.push({ start: m.index, end: m.index + v.length, text: v });
    }
  }
  const matches = [...regs, ...nums].sort((a, b) => a.start - b.start);

  // Remove overlapping (prefer first)
  const filtered = [];
  let cursor = 0;
  for (const x of matches) {
    if (x.start < cursor) continue;
    filtered.push(x);
    cursor = x.end;
  }

  filtered.forEach((x, i) => {
    if (last < x.start) pieces.push(text.slice(last, x.start));
    pieces.push(<strong key={`b-${i}`}>{x.text}</strong>);
    last = x.end;
  });
  if (last < text.length) pieces.push(text.slice(last));
  return pieces;
};

/**
 * Renders a multi-sentence risk-card narrative.
 *
 * Per the spec: bold ONLY the single first sentence stating the primary risk
 * driver. Subsequent sentences are regular weight, with inline bolding via
 * renderBolded preserved for named regulations / %s / $s only.
 */
export const renderNarrative = (text) => {
  if (!text) return null;
  const sentMatch = text.match(/^([^.!?]+[.!?])\s*(.*)$/s);
  if (!sentMatch) {
    // No sentence terminator — bold the whole short string (it's one short clause).
    return <strong>{renderBolded(text)}</strong>;
  }
  const [, firstSentence, rest] = sentMatch;
  return (
    <>
      <strong>{renderBolded(firstSentence)}</strong>
      {rest ? <> {renderBolded(rest)}</> : null}
    </>
  );
};

/**
 * Recommendation rendering: bold the leading imperative verb phrase
 * (everything up to the first comma, semicolon, "to" or end of first clause).
 */
export const renderRecommendation = (text) => {
  if (!text) return null;
  const m = text.match(/^(\w+(?:\s+\w+){0,2})(\s.*)?$/);
  if (!m) return renderBolded(text);
  return (
    <>
      <strong>{m[1]}</strong>
      {m[2] ? <> {renderBolded(m[2].trimStart())}</> : null}
    </>
  );
};
